import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';

const request = async (method, url) => {
  const response = await fetch(url, {
    method,
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || 'The adaptive reading records could not be loaded.');
  }
  return payload;
};

const completedAttempts = (quiz) => (
  (Array.isArray(quiz?.attempts) ? quiz.attempts : [])
    .filter((attempt) => attempt.status === 'completed')
);

const formatScore = (attempt, t) => {
  if (!attempt || attempt.total === null || attempt.total === undefined) return t('Not completed');
  const accuracy = attempt.accuracy ?? (
    attempt.total > 0 ? Math.round((attempt.correct / attempt.total) * 1000) / 10 : 0
  );
  return `${attempt.correct}/${attempt.total} (${accuracy}%)`;
};

export default function AdminStudentAdaptiveReadingList({ classId, studentId }) {
  const { locale, t } = useI18n();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState('');
  const [deletingId, setDeletingId] = useState('');

  const baseUrl = useMemo(() => (
    `/api/admin/classes/${classId}/students/${studentId}/quiz-assignments`
  ), [classId, studentId]);

  const load = useCallback(async () => {
    if (!classId || !studentId) return;
    setLoading(true);
    setError('');
    try {
      const payload = await request('GET', baseUrl);
      setRecords(Array.isArray(payload.assigned) ? payload.assigned : []);
    } catch (loadError) {
      setError(loadError.message || 'The adaptive reading records could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [baseUrl, classId, studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (value) => {
    const date = new Date(value || '');
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(locale === 'zh-CN' ? 'zh-CN' : 'en', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const deletePractice = async (quizId) => {
    setDeletingId(quizId);
    setError('');
    try {
      await request('DELETE', `${baseUrl}/${quizId}`);
      setConfirmDeleteId('');
      setExpandedId('');
      await load();
    } catch (deleteError) {
      setError(deleteError.message || 'The practice could not be deleted.');
    } finally {
      setDeletingId('');
    }
  };

  if (loading) return <p className="py-4 text-sm text-gray-500">{t('Loading...')}</p>;

  return (
    <div>
      {error && (
        <div className="mb-3 border-l-4 border-red-500 bg-red-50 px-3 py-2 text-sm text-red-800">
          {t(error)}
        </div>
      )}
      {records.length === 0 ? (
        <p className="py-4 text-sm text-gray-500">{t('No adaptive reading records yet.')}</p>
      ) : (
        <div className="overflow-x-auto border-y border-gray-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-600">
              <tr>
                <th className="px-3 py-3">{t('Practice name')}</th>
                <th className="px-3 py-3">{t('First Score')}</th>
                <th className="px-3 py-3">{t('Latest Score')}</th>
                <th className="px-3 py-3">{t('Attempts')}</th>
                <th className="px-3 py-3 text-right">{t('Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {records.map((quiz) => {
                const attempts = completedAttempts(quiz);
                const latest = attempts[0];
                const first = attempts[attempts.length - 1];
                const expanded = expandedId === quiz.id;
                const confirming = confirmDeleteId === quiz.id;
                return (
                  <React.Fragment key={quiz.id}>
                    <tr className="align-top">
                      <td className="px-3 py-3 font-semibold text-gray-900" data-i18n-skip>{quiz.name}</td>
                      <td className="px-3 py-3 text-gray-700">{formatScore(first, t)}</td>
                      <td className="px-3 py-3 text-gray-700">{formatScore(latest, t)}</td>
                      <td className="px-3 py-3 text-gray-700">{quiz.attempt_count || 0}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setExpandedId(expanded ? '' : quiz.id)}
                            className="inline-flex h-9 items-center gap-1 rounded border border-gray-300 px-3 font-semibold text-gray-700 hover:bg-gray-50"
                          >
                            {expanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                            {expanded ? t('Collapse') : t('Expand')}
                          </button>
                          {!confirming && (
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteId(quiz.id)}
                              className="inline-flex h-9 items-center gap-1 rounded border border-red-200 px-3 font-semibold text-red-700 hover:bg-red-50"
                            >
                              <TrashIcon className="h-4 w-4" />
                              {t('Delete practice')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {confirming && (
                      <tr className="bg-red-50">
                        <td colSpan={5} className="px-3 py-3">
                          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                            <p className="text-sm text-red-800">
                              {t("This deletes the assignment and all of this student's related responses.")}
                            </p>
                            <div className="flex shrink-0 gap-2">
                              <button
                                type="button"
                                disabled={deletingId === quiz.id}
                                onClick={() => setConfirmDeleteId('')}
                                className="h-9 rounded border border-gray-300 bg-white px-3 font-semibold text-gray-700"
                              >
                                {t('Cancel')}
                              </button>
                              <button
                                type="button"
                                disabled={deletingId === quiz.id}
                                onClick={() => deletePractice(quiz.id)}
                                className="h-9 rounded bg-red-600 px-3 font-semibold text-white disabled:opacity-50"
                              >
                                {deletingId === quiz.id ? t('Deleting...') : t('Confirm delete')}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    {expanded && (
                      <tr>
                        <td colSpan={5} className="bg-gray-50 px-3 py-3">
                          {attempts.length === 0 ? (
                            <p className="text-sm text-gray-500">{t('No completed attempts')}</p>
                          ) : (
                            <div className="divide-y divide-gray-200 border-y border-gray-200 bg-white">
                              {attempts.map((attempt, index) => (
                                <div key={attempt.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-3">
                                  <div>
                                    <p className="font-semibold text-gray-900">
                                      {t('Attempt {number}', { number: attempts.length - index })}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-500">
                                      {formatDate(attempt.completed_at || attempt.created_at)} · {formatScore(attempt, t)}
                                    </p>
                                  </div>
                                  {attempt.result_id && (
                                    <a
                                      href={`/review/attempt/${attempt.result_id}?adminView=1`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="rounded border border-indigo-200 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
                                    >
                                      {t('View details')}
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
